export function formatAssignmentDate(value) {
  if (!value) {
    return "Not set";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "Not set"
    : date.toLocaleString();
}

const ACTIVITY_TARGET_FIELDS = [
  "activityName",
  "title",
  "name",
  "displayName",
  "label",
  "titleKey",
  "labelKey",
  "taskKey",
];

const ACRONYM_LABELS = {
  "2afc": "2AFC",
  afc: "AFC",
  capev: "CAPE-V",
  capevr: "CAPE-Vr",
  osrboi: "OSRBOI",
  vas: "VAS",
  vrp: "VRP",
  wvd: "WVD",
};

const ASSIGNMENT_LIFECYCLE_LABELS = {
  draft: "Draft",
  published: "Published",
  closed: "Closed",
  archived: "Archived",
  cancelled: "Cancelled",
};

const ASSIGNMENT_LIFECYCLE_TONES = {
  draft: "warning",
  published: "success",
  closed: "information",
  archived: "information",
  cancelled: "danger",
};

const PARTICIPANT_PROGRESS_LABELS = {
  not_started: "Not started",
  in_progress: "In progress",
  submitted: "Submitted",
  completed: "Completed",
};

const PARTICIPANT_ASSIGNMENT_ERROR_MESSAGES = {
  ASSIGNMENT_ATTEMPT_COMPLETED:
    "This assignment is already complete.",
  ASSIGNMENT_ATTEMPT_SUBMITTED:
    "Your responses have been submitted. Return to the assignment details to finish the assignment.",
  ASSIGNMENT_CLOSED:
    "This assignment is closed. You cannot start, resume, or submit responses.",
  ASSIGNMENT_CONTEXT_INVALID:
    "This assignment run could not be verified. Reopen it from Assigned Work.",
  ASSIGNMENT_CONTEXT_MISMATCH:
    "This assignment run could not be verified. Reopen it from Assigned Work.",
  ASSIGNMENT_NOT_AVAILABLE:
    "This assignment is not available for participation.",
  ASSIGNMENT_NOT_FOUND:
    "This assigned work could not be found.",
  ASSIGNMENT_NOT_YET_AVAILABLE:
    "This assignment is not available yet. Check the availability date.",
  ASSIGNMENT_OPERATION_FAILED:
    "This assignment action could not be completed. Please try again.",
  ATTEMPT_INCOMPLETE:
    "Answer all required items before finishing this assignment.",
  ATTEMPT_NOT_STARTED:
    "Start the assignment before finishing it.",
  AUTHENTICATION_REQUIRED:
    "Sign in again to continue this assigned work.",
  EVALUATION_TASK_NOT_SELECTABLE_FOR_ASSIGNMENT:
    "This assigned activity is no longer available. Return to Assigned Work and try again.",
  EVALUATION_TASK_NOT_SUPPORTED_FOR_ASSIGNMENT:
    "This assigned activity is not supported by the current assigned-work runtime.",
  FORBIDDEN:
    "This assignment is not available for your account.",
  NOT_FOUND:
    "This assigned work could not be found.",
  PARTICIPANT_ATTEMPT_NOT_FOUND:
    "This assignment progress could not be found. Reopen it from Assigned Work.",
  PARTICIPANT_ATTEMPT_REQUIRED:
    "This assignment progress could not be found. Reopen it from Assigned Work.",
};

function isObjectIdLike(value) {
  return /^[a-f0-9]{24}$/i.test(value);
}

function normalizeKnownAcronyms(value) {
  return value
    .replace(/cape[-_\s]?vr/gi, "capevr")
    .replace(/cape[-_\s]?v/gi, "capev")
    .replace(/2[-_\s]?afc/gi, "2afc");
}

function formatToken(token) {
  const normalized = token.toLowerCase();

  if (ACRONYM_LABELS[normalized]) {
    return ACRONYM_LABELS[normalized];
  }

  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
}

function humanizeIdentifier(value) {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();

  if (!trimmed || isObjectIdLike(trimmed)) {
    return "";
  }

  const withoutKeySuffix =
    normalizeKnownAcronyms(trimmed)
      .replace(/[-_\s]+(?:title|label|task|key)$/i, "")
      .replace(/[-_\s]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  if (!withoutKeySuffix || isObjectIdLike(withoutKeySuffix)) {
    return "";
  }

  return withoutKeySuffix
    .split(" ")
    .map(formatToken)
    .join(" ");
}

function fallbackLabel(value) {
  if (!value) {
    return "";
  }

  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();

  return isObjectIdLike(trimmed)
    ? ""
    : trimmed;
}

export function targetLabel(target = {}) {
  const source = target || {};

  for (const field of ACTIVITY_TARGET_FIELDS) {
    const explicitLabel =
      ["activityName", "title", "name", "displayName", "label"].includes(field)
        ? fallbackLabel(source[field])
        : humanizeIdentifier(source[field]);

    if (explicitLabel) {
      return explicitLabel;
    }
  }

  return "Activity";
}

export function assignmentLifecycleLabel(status) {
  if (ASSIGNMENT_LIFECYCLE_LABELS[status]) {
    return ASSIGNMENT_LIFECYCLE_LABELS[status];
  }

  return humanizeIdentifier(status) || "Unknown";
}

export function assignmentLifecycleTone(status) {
  return ASSIGNMENT_LIFECYCLE_TONES[status] || "information";
}

export function participantProgressLabel(progress = {}) {
  const state = progress || {};
  const status = state.status || "not_started";

  if (
    state.isClosed &&
    status !== "completed" &&
    status !== "submitted"
  ) {
    return "Closed";
  }

  if (state.isUpcoming && status === "not_started") {
    return "Upcoming";
  }

  const baseLabel =
    PARTICIPANT_PROGRESS_LABELS[status] ||
    humanizeIdentifier(status) ||
    "Not started";

  if (
    state.isOverdue &&
    status !== "submitted" &&
    status !== "completed"
  ) {
    return `${baseLabel} · Overdue`;
  }

  return baseLabel;
}

export function participantActionLabel(progress = {}) {
  const state = progress || {};

  if (state.status === "completed") {
    return "Completed";
  }

  if (state.status === "submitted") {
    return "Finish assignment";
  }

  if (state.isClosed) {
    return "Closed";
  }

  if (state.isUpcoming) {
    return "Upcoming";
  }

  return state.status === "in_progress"
    ? "Resume assignment"
    : "Start assignment";
}

export function participantStatusMessage(progress = {}) {
  const state = progress || {};

  if (state.status === "completed") {
    return "Assignment complete. Your responses are saved, and no further action is needed.";
  }

  if (state.status === "submitted") {
    return "Responses submitted. Finish the assignment to mark it complete.";
  }

  if (state.isClosed) {
    return "This assignment is closed. You cannot start, resume, or submit responses.";
  }

  if (state.isUpcoming) {
    return "This assignment is not available yet. Check the availability date above.";
  }

  return "";
}

export function participantStatusDetail(progress = {}) {
  const state = progress || {};

  if (state.status === "completed") {
    return "Return to Assigned Work to review your other activities, or go back to your Dashboard.";
  }

  if (state.status === "submitted") {
    return "Your responses are saved. Finishing marks this assigned activity complete.";
  }

  if (state.isClosed) {
    return "If you think this is a mistake, contact the facilitator.";
  }

  return "";
}

export function participantAssignmentErrorMessage(error, fallback) {
  const code =
    error?.response?.data?.error;

  return (
    PARTICIPANT_ASSIGNMENT_ERROR_MESSAGES[code] ||
    fallback
  );
}

export function monitoringStatusLabel(progress = {}) {
  return participantProgressLabel(progress);
}
